import {
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import Markdown from 'react-markdown';

import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import { useSubscription } from '../../SubscriptionProvider';
import { createSubscriptionCheckoutSession } from '../../../../utils/service';
import { getGenericError } from '../../../../utils/errors';
import CouponCodeInput from '../../../../global/CouponCodeInput';
import StripeCustomCheckout from '../../../../global/StripeElements/StripeCustomCheckout';
import {
  SUBSCRIPTION_STEPS,
  getSubscriptionOrderItemsFromStorage,
  SUBSCRIPTION_PAYMENT_TYPES,
} from '../../../../utils/subscriptions';
import {
  SUBSCRIPTION_TRACKER_TYPES,
  TRACKER_IDS,
  getTrackerIdEventName,
} from '../../../../config/tracker';
import { MEMBERSHIP_TERMS_URL } from '../../../../config/externalURL';
import { ROUTE_HOME } from '../../../../config/routes';
import MembershipOrderTbbContainer from '../../../MembershipOrderTbb/MembershipOrderTbbContainer';
import MEMBERSHIP_OFFERED_ITEMS, {
  getFilteredMembershipItems,
  MEMBERSHIP_DEFAULT_ORDER_ITEMS,
} from '../../../../utils/membership';
import { isMobile, SIZE_XS, SIZE_SM, SIZE_MD } from '../../../../../../hooks/useBreakPoint';
import { MEMBERSHIP_ORDER_TBB_STEPS, MEMBERSHIP_ORDER_TBB_TYPE } from '../../../MembershipOrderTbb/MembershipOrderTbbContext';
import { pushSubscriptionStartedEvent } from '../../../../utils/gtm';
import { formatCurrencyNumber } from '../../../../utils/numbers';
import LoadingBar from '../../../../global/LoadingBar';
import trashieLogo from '../../../../images/trashie-logo.webp';
import mobileNavbarLogo from '../../../../images/subscription-navbar-mobile-logo.svg';
import mobileSummaryChevronIcon from '../../../../images/chevron-right-figma.svg';
import unlimitedBadgeFigmaIcon from '../../../../images/unlimited-badge-figma.svg';
import canCreateSubscriptionCheckoutSession from './checkoutSessionGate';

import SubscriptionOrderSummary, { SubscriptionOrderSummaryLegal } from './SubscriptionOrderSummary';

import './SubscriptionUnlimitedCheckout.scss';

const MOBILE_SUMMARY_UNLIMITED_BADGE_SRC = unlimitedBadgeFigmaIcon;

const {
  subscription: {
    payment: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
    },
  },
} = TRACKER_IDS;

const CouponTagIcon = () => (
  <svg
    className="SubscriptionUnlimitedCheckout__couponToggleIcon"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6h.008v.008H6V6z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SubscriptionUnlimitedCheckoutLegacy = () => {
  const hasTrackedInitiateCheckout = useRef(false);
  const hasTrackedSubscriptionStarted = useRef(false);
  const { userData } = useFirebaseAuth();
  const {
    config: subscriptionConfig,
    subscriptionPlan,
    posthog,
    isAnonymous,
    searchParams,
  } = useSubscription();

  const isNarrowViewport = isMobile([SIZE_XS, SIZE_SM, SIZE_MD]);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [couponSectionOpen, setCouponSectionOpen] = useState(false);

  const [products, setProducts] = useState(
    () => getSubscriptionOrderItemsFromStorage() || MEMBERSHIP_DEFAULT_ORDER_ITEMS,
  );
  const [address, setAddress] = useState(null);
  const [checkoutEnabled, setCheckoutEnabled] = useState(true);
  const [errorStripe, setErrorStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoadingClientSecret, setIsLoadingClientSecret] = useState(true);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponErrorMessage, setCouponErrorMessage] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [discountedValue, setDiscountedValue] = useState(null);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [draftAddress, setDraftAddress] = useState(null);

  const {
    value: subscriptionValue,
    interval: subscriptionInterval = 'yr',
  } = subscriptionPlan || {};

  const membershipDisplayPrice = typeof subscriptionValue === 'number' && subscriptionValue >= 0
    ? subscriptionValue
    : 68;

  const effectiveAddress = draftAddress ?? address;
  const canCreateCheckoutSession = canCreateSubscriptionCheckoutSession(
    products,
    effectiveAddress,
  );

  const info = subscriptionConfig?.steps?.[SUBSCRIPTION_STEPS.PAYMENT]?.info || '';

  const fetchClientSecret = useCallback(async (
    productsData,
    addressData,
    couponId = null,
  ) => {
    setIsLoadingClientSecret(true);
    setClientSecret(null);
    setCouponMessage('');
    setCouponErrorMessage('');

    const membershipItems = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.SUBSCRIPTION)
      .map((item) => item.id);

    const mappedProducts = Object.entries(productsData)
      .filter(
        ([productType, quantity]) => membershipItems.includes(productType) && quantity > 0,
      )
      .map(([productType, quantity]) => ({
        productType: MEMBERSHIP_OFFERED_ITEMS[productType].id,
        quantity,
      }));

    const {
      data,
      error: createCheckoutSessionError,
    } = await createSubscriptionCheckoutSession({
      products: mappedProducts,
      address: addressData,
      couponId,
      searchParams,
    });

    if (createCheckoutSessionError || !data?.clientSecret) {
      setErrorStripe(getGenericError());
      setClientSecret(null);
      setCheckoutEnabled(false);
      setIsLoadingClientSecret(false);
      return;
    }

    const appliedCoupon = Boolean(couponId && data.couponApplied);

    setClientSecret(data?.clientSecret ?? null);
    setErrorStripe(null);
    setIsCouponApplied(Boolean(couponId && data.couponApplied));
    setDiscountedValue(data?.discountedValue ?? null);

    if (couponId) {
      if (appliedCoupon) {
        setCouponMessage('Coupon applied successfully');
        setCouponErrorMessage('');
      } else {
        setCouponMessage('');
        setCouponErrorMessage('Invalid coupon');
      }
    }

    setCouponCode('');
    setCheckoutEnabled(true);
    setIsLoadingClientSecret(false);
    if (!hasTrackedInitiateCheckout.current) {
      window.fbq?.('track', 'InitiateCheckout');
      hasTrackedInitiateCheckout.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    if (address) {
      setDraftAddress(null);
    }
  }, [address]);

  useEffect(() => {
    if (!canCreateCheckoutSession) {
      setClientSecret(null);
      setCheckoutEnabled(false);
      setIsLoadingClientSecret(false);
      setErrorStripe(null);
      return;
    }
    fetchClientSecret(products, effectiveAddress, appliedCouponCode);
  }, [
    appliedCouponCode,
    canCreateCheckoutSession,
    effectiveAddress,
    fetchClientSecret,
    products,
  ]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
  }, [posthog]);

  useEffect(() => {
    if (hasTrackedSubscriptionStarted.current) {
      return;
    }
    hasTrackedSubscriptionStarted.current = true;
    pushSubscriptionStartedEvent(userData);
  }, [userData]);

  useEffect(() => {
    if (couponMessage || couponErrorMessage) {
      setCouponSectionOpen(true);
    }
  }, [couponMessage, couponErrorMessage]);

  const summaryProps = {
    membershipDisplayPrice,
    subscriptionInterval,
    isCouponApplied,
    fullValue: membershipDisplayPrice,
    discountedValue,
  };

  let paymentSectionBody;
  if (clientSecret) {
    paymentSectionBody = (
      <StripeCustomCheckout
        clientSecret={clientSecret}
        fullValue={membershipDisplayPrice}
        discountedValue={discountedValue}
        isCouponApplied={isCouponApplied}
        disabled={!checkoutEnabled}
      />
    );
  } else if (isLoadingClientSecret) {
    paymentSectionBody = (
      <div
        className="SubscriptionUnlimitedCheckout__paymentLoading"
        role="status"
        aria-live="polite"
      >
        <LoadingBar />
      </div>
    );
  } else if (!canCreateCheckoutSession) {
    paymentSectionBody = (
      <p className="SubscriptionUnlimitedCheckout__paymentHint">
        Enter a valid delivery address to load payment.
      </p>
    );
  } else {
    paymentSectionBody = (
      <p className="SubscriptionUnlimitedCheckout__paymentHint">
        Payment could not be loaded. Check the message above or refresh the page.
      </p>
    );
  }

  const deliverySection = (
    <div className="SubscriptionUnlimitedCheckout__formPadded SubscriptionUnlimitedCheckout__formPadded--delivery">
      <div className="SubscriptionUnlimitedCheckout__shippingNote" role="status">
        Not available in Alaska, Hawaii and Puerto Rico (yet!)
      </div>

      <MembershipOrderTbbContainer
        containerClassName="SubscriptionUnlimitedCheckout__section"
        defaultValues={
          getSubscriptionOrderItemsFromStorage() || MEMBERSHIP_DEFAULT_ORDER_ITEMS
        }
        defaultStep={MEMBERSHIP_ORDER_TBB_STEPS.ADDRESSES_CONTAINER}
        orderType={MEMBERSHIP_ORDER_TBB_TYPE.MEMBERSHIP_FIRST}
        isAnonymous={isAnonymous}
        useWhiteCards
        hideOrderCta
        addressOnly
        addressFormSubmitButtonText="Next"
        newAddressFormTitle="Delivery"
        designSystemForms
        stayOnAddressFormAfterSave
        hideAddressFormBackLink
        onValidAddressChange={setDraftAddress}
        onProductsChange={setProducts}
        onAddressChange={setAddress}
      />

      <div className="SubscriptionUnlimitedCheckout__section SubscriptionUnlimitedCheckout__section--coupon">
        <button
          type="button"
          className="SubscriptionUnlimitedCheckout__couponToggle"
          onClick={() => setCouponSectionOpen((open) => !open)}
          aria-expanded={couponSectionOpen}
        >
          <CouponTagIcon />
          <span>
            {couponSectionOpen ? 'Hide coupon code' : 'Add coupon code'}
          </span>
        </button>
        {couponSectionOpen && (
          <div className="SubscriptionUnlimitedCheckout__couponFields">
            <CouponCodeInput
              value={couponCode}
              onChange={setCouponCode}
              onApply={() => setAppliedCouponCode(couponCode)}
              disabled={isLoadingClientSecret}
              applyDisabled={!couponCode || isLoadingClientSecret}
              successMessage={couponMessage}
              errorMessage={couponErrorMessage}
              designSystem
            />
          </div>
        )}
        {errorStripe && (
          <div className="SubscriptionUnlimitedCheckout__stripeError">{errorStripe}</div>
        )}
      </div>
    </div>
  );

  const couponStripeSeparator = (
    <div className="SubscriptionUnlimitedCheckout__couponStripeSeparator" aria-hidden="true" />
  );

  const stripeSection = (
    <div className="SubscriptionUnlimitedCheckout__section SubscriptionUnlimitedCheckout__section--stripe">
      {paymentSectionBody}
    </div>
  );

  const termsSection = (
    <div className="SubscriptionUnlimitedCheckout__formPadded SubscriptionUnlimitedCheckout__formPadded--terms">
      <div className="SubscriptionUnlimitedCheckout__terms">
        {info && (
          <Markdown>{info}</Markdown>
        )}
        By completing this purchase, you agree to our
        {' '}
        <a
          href={MEMBERSHIP_TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View terms of service"
        >
          Terms of service
        </a>
        .
      </div>
    </div>
  );

  return (
    <div className="SubscriptionUnlimitedCheckout">
      <div className="SubscriptionUnlimitedCheckout__topFixed">
        <header className="SubscriptionUnlimitedCheckout__headerBar">
          <Link className="SubscriptionUnlimitedCheckout__logoLink" to={ROUTE_HOME}>
            <img
              className="SubscriptionUnlimitedCheckout__logo"
              src={isNarrowViewport ? mobileNavbarLogo : trashieLogo}
              alt="Trashie"
            />
          </Link>
        </header>
      </div>

      <div className="SubscriptionUnlimitedCheckout__shell">
        {isNarrowViewport && (
          <div className="SubscriptionUnlimitedCheckout__mobileSummary">
            <button
              type="button"
              className="SubscriptionUnlimitedCheckout__mobileSummaryToggle"
              onClick={() => setSummaryExpanded((v) => !v)}
              aria-expanded={summaryExpanded}
            >
              <span className="SubscriptionUnlimitedCheckout__mobileSummaryLead">
                <span className="SubscriptionUnlimitedCheckout__mobileSummaryBadge" aria-hidden>
                  <img
                    src={MOBILE_SUMMARY_UNLIMITED_BADGE_SRC}
                    alt=""
                    className="SubscriptionUnlimitedCheckout__mobileSummaryBadgeIcon"
                  />
                </span>
                <span className="SubscriptionUnlimitedCheckout__mobileSummaryTitleRow">
                  <span className="SubscriptionUnlimitedCheckout__mobileSummaryMembershipLabel">
                    Unlimited Membership
                  </span>
                  <span className="SubscriptionUnlimitedCheckout__mobileSummaryPrice">
                    {formatCurrencyNumber(
                      isCouponApplied && discountedValue != null
                        ? discountedValue
                        : membershipDisplayPrice,
                    )}
                  </span>
                </span>
              </span>
              <img
                src={mobileSummaryChevronIcon}
                alt=""
                aria-hidden
                className={classNames('SubscriptionUnlimitedCheckout__mobileSummaryChevron', {
                  'SubscriptionUnlimitedCheckout__mobileSummaryChevron--expanded': summaryExpanded,
                })}
              />
            </button>
            <div
              className={classNames('SubscriptionUnlimitedCheckout__mobileSummaryCollapse', {
                'SubscriptionUnlimitedCheckout__mobileSummaryCollapse--expanded': summaryExpanded,
              })}
              aria-hidden={!summaryExpanded}
            >
              <div className="SubscriptionUnlimitedCheckout__mobileSummaryCollapseInner">
                <div className="SubscriptionUnlimitedCheckout__mobileSummaryBody">
                  <SubscriptionOrderSummary {...summaryProps} hideLegal hideCardTitle />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="SubscriptionUnlimitedCheckout__grid">
          <div className="SubscriptionUnlimitedCheckout__main">
            <div className="SubscriptionUnlimitedCheckout__form">
              {deliverySection}
              {couponStripeSeparator}
              {stripeSection}
              {termsSection}
            </div>
          </div>

          {!isNarrowViewport && (
            <>
              <div className="SubscriptionUnlimitedCheckout__gridDivider" aria-hidden="true" />
              <SubscriptionOrderSummary
                className="SubscriptionUnlimitedCheckout__aside"
                {...summaryProps}
              />
            </>
          )}
        </div>

        {isNarrowViewport && (
          <div className="SubscriptionUnlimitedCheckout__mobileLegalFooter">
            <SubscriptionOrderSummaryLegal />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionUnlimitedCheckoutLegacy;
